/**
 * Referral code capture and attribution.
 *
 * Mirrors the UTM capture/attribution split in use-utm.ts / use-utm-attribution.ts:
 *  - `useReferralCapture()` reads `?ref=CODE` from the landing page URL and
 *    persists it to localStorage so it survives the Clerk sign-in modal.
 *  - `useReferralAttribution()` runs once after sign-in and POSTs the stored
 *    code to the backend, which records the referral relationship. The
 *    backend is idempotent (a user can only be attributed once), so this is
 *    safe to fire on every sign-in, not just the first.
 *
 * `useReferralCapture()` also reports the tap to POST /referrals/click
 * (fire-and-forget, IP + platform fingerprinted). This is only the fallback
 * for a friend who taps a referral link without the mobile app installed yet.
 * Once the store-published app has verified Universal Links/App Links and a
 * supported deferred-link provider is configured, that path should carry the
 * code through install instead; see the mobile hook for the fallback order.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/react';

export const REFERRAL_STORAGE_KEY = 'mm_referral_code';

function parseReferralCode(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('ref');
  return code ? code.trim().toUpperCase() : null;
}

/** Best-guess platform from the user agent, for the click-fingerprint match. */
function detectPlatform(): 'ios' | 'android' | 'unknown' {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'unknown';
}

export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Capture `?ref=CODE` from the URL on first render and persist it. */
export function useReferralCapture(): void {
  useEffect(() => {
    const code = parseReferralCode();
    if (!code) return;
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, code);
    } catch {
      // localStorage may be unavailable in private browsing; fail silently.
    }

    // Best-effort deferred-attribution capture — see doc comment above.
    // Never blocks rendering and never surfaces an error to the visitor.
    fetch('/api/referrals/click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, platform: detectPlatform() }),
    }).catch(() => {
      // Network failure — the "already installed" attribution path below is
      // unaffected either way.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** After sign-in, submit any stored referral code to the backend once. */
export function useReferralAttribution(): void {
  const { isSignedIn, getToken } = useAuth();
  const attempted = useRef(false);

  useEffect(() => {
    if (!isSignedIn || attempted.current) return;

    const code = getStoredReferralCode();
    if (!code) return;

    attempted.current = true;

    (async () => {
      try {
        const token = await getToken();
        const res = await fetch('/api/referrals/attribute', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });

        if (!res.ok) {
          // Transient failure (5xx, an auth hiccup, etc.) — not a definitive
          // outcome. Keep the code stored so a later mount/session retries
          // within the signup window instead of silently losing attribution.
          attempted.current = false;
          return;
        }

        // 2xx is a definitive outcome either way — attributed, or a business
        // reason not to be (self-referral, already attributed, window
        // expired, etc.) — so stop retrying and clear the stored code.
        try {
          localStorage.removeItem(REFERRAL_STORAGE_KEY);
        } catch {
          // ignore
        }
      } catch {
        // Network-level failure; leave the code stored so a later mount can retry.
        attempted.current = false;
      }
    })();
  }, [isSignedIn, getToken]);
}
